import type { WriterToolActivity } from '@/hooks/useWriterThread';

/**
 * One source of truth for what Writer is currently doing.
 *
 * The bubble's status line, the "Work details" chip, and the chat header all
 * read these phrases so they can never name the same step differently.
 */
export type WriterToolBucket = 'edits' | 'research' | 'diagnostics' | 'other';

export const TOOL_BUCKET: Record<string, WriterToolBucket> = {
  patch_section: 'edits',
  replace_section: 'edits',
  create_section: 'edits',
  set_preamble: 'edits',
  add_to_bib: 'edits',
  materialize: 'edits',
  insert_figure: 'edits',
  format_tex: 'edits',
  apply_template: 'edits',
  remember: 'other',
  search_literature: 'research',
  resolve_citation: 'research',
  plot_from_data: 'research',
  generate_diagram: 'research',
  table_from_data: 'research',
  image_to_tikz: 'research',
  paper_map: 'diagnostics',
  list_sections: 'diagnostics',
  read_section: 'diagnostics',
  validate_document: 'diagnostics',
  calculate: 'diagnostics',
  derive: 'diagnostics',
  check_math: 'diagnostics',
  check_claims: 'diagnostics',
};

export const WRITER_PHASE_LABEL: Record<WriterToolBucket, string> = {
  research: 'Finding evidence',
  diagnostics: 'Reviewing the draft',
  edits: 'Updating the manuscript',
  other: 'Preparing the response',
};

/** Used before any step has run: honest, and it claims nothing about the model. */
export const WRITER_WORKING_LABEL = 'Writer is working';

/** Used once reply text is arriving, which is the one moment we can prove it. */
export const WRITER_REPLY_LABEL = 'Writing the reply';

/** Phase name the server stores while handing a long request to a fresh run. */
export const WRITER_CONTINUING_PHASE = 'continuing';
export const WRITER_CONTINUING_LABEL = 'Continuing the work';

export function writerToolBucket(name: string): WriterToolBucket {
  return TOOL_BUCKET[name] ?? 'other';
}

/**
 * Phase of the step that is running, otherwise of the most recent step.
 * Falling back to the last step keeps the line steady between two steps
 * instead of flashing a generic phrase back and forth.
 */
export function writerPhaseLabel(activities: WriterToolActivity[]): string | null {
  const running = activities.find((activity) => activity.status === 'running');
  const current = running ?? activities[activities.length - 1];
  if (!current) return null;
  if (current.name === WRITER_CONTINUING_PHASE) return WRITER_CONTINUING_LABEL;
  return WRITER_PHASE_LABEL[writerToolBucket(current.name)];
}

export interface WriterTurnStatusInput {
  streaming: boolean;
  hasProse: boolean;
  activities?: WriterToolActivity[];
  /** The AI service has sent nothing for a while on the current step. */
  aiSlow?: boolean;
}

/** Shown when the AI service is slow to answer, so a long wait doesn't look stuck. */
export const WRITER_AI_SLOW_LABEL = 'Waiting for the AI service';

export interface WriterTurnStatus {
  /** True while the bubble should show the line in place of reply prose. */
  visible: boolean;
  label: string;
  showSpinner: boolean;
}

export function writerTurnStatus({
  streaming,
  hasProse,
  activities = [],
  aiSlow = false,
}: WriterTurnStatusInput): WriterTurnStatus {
  if (!streaming) return { visible: false, label: '', showSpinner: false };
  if (hasProse) return { visible: false, label: WRITER_REPLY_LABEL, showSpinner: true };
  const running = activities.some((a) => a.status === 'running');
  return {
    visible: true,
    label: aiSlow && !running ? WRITER_AI_SLOW_LABEL : writerPhaseLabel(activities) ?? WRITER_WORKING_LABEL,
    showSpinner: true,
  };
}
